<template>
  <v-form ref="form" v-model="valid" method="post" @submit.prevent="onSubmit">
    <v-card>
    <v-container fluid>
      <v-row>
        <v-col cols="12" lg="6">
          <v-card-title>Initial Setup</v-card-title>
          <v-card-subtitle>Specify completion and production data</v-card-subtitle>
          <v-card-text>
            <v-text-field label="Name of dataset" v-model="dataset.name" required :rules="rules"></v-text-field>
            <v-autocomplete
              v-if="files"
              :items="file_names"
              v-model="dataset.completion"
              item-text="name"
              item-value="uuid"
              clearable
              return-object
              label="Completion data"
              :rules="rules"
            />
            <v-autocomplete
              v-if="files"
              :items="file_names"
              v-model="dataset.production"
              clearable
              return-object
              item-text="name"
              item-value="uuid"
              label="Production data"
            />
            <v-autocomplete
              v-if="files"
              :items="file_names"
              v-model="dataset.economic"
              clearable
              return-object
              item-text="name"
              item-value="uuid"
              label="Economic data"
            />
            <v-autocomplete label="Unique Well Identifier" :items="well_identifier" v-model="dataset.identifier" :rules="rules" clearable />
          </v-card-text>
        </v-col>
        <v-col cols="12" lg="6">
          <v-card-title>Data Filters</v-card-title>
          <v-card-subtitle>Specify filters limiting the scope of data (optional)</v-card-subtitle>
          <v-card-text>
            <v-card outlined>
              <v-toolbar dense flat tile color="grey" :class="isDark ? 'darken-3' : 'lighten-2'">
                <filters :dataset="dataset" :files="filter_files" :columns="columns" :disabled="!isValidStep1" @submitted="onAddFilter" />
                <v-btn tile icon :disabled="selected === undefined" @click="deleteItem"><v-icon>mdi-minus</v-icon></v-btn>
                <v-btn tile icon @click="insertOperator('and')">AND</v-btn>
                <v-btn tile icon @click="insertOperator('or')">OR</v-btn>
                <v-btn tile icon @click="insertOperator('(')">(</v-btn>
                <v-btn tile icon @click="insertOperator(')')">)</v-btn>
                <v-btn tile icon>
                  <v-icon :color="validated === undefined ? 'grey' : validated ? 'success' : 'error'">
                    {{ validated === undefined ? "mdi-help-circle-outline" : validated ? "mdi-check-circle-outline" : "mdi-alert-circle-outline" }}
                  </v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-text>
                <v-chip-group column v-model="selected" active-class="primary">
                  <template v-if="dataset.filters">
                    <draggable v-model="dataset.filters" @start="drag = true" @end="drag = false">
                      <v-chip v-for="(filter, index) in dataset.filters" :key="index" label>
                        <template v-if="isObject(filter)">
                          {{ filter.column }} {{ filter.negation ? "not" : "" }} {{ filter.operator }}
                          {{ filter_value(filter) }}
                        </template>
                        <template v-else>{{ filter }}</template>
                      </v-chip>
                    </draggable>
                  </template>
                  <template v-else>No filters</template>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-col>
      </v-row>
    </v-container>
    <v-card-actions>
      <v-btn depressed color="success" type="submit" :disabled="!valid" :loading="working">
        {{ mode == "edit" ? "Update" : "Compile" }}
      </v-btn>
      <v-btn text @click="$router.go(-1)">Cancel</v-btn>
    </v-card-actions>
  </v-card>
  </v-form>
</template>
<script>
import AxDialog from "@/components/dialog"
import Filters from "@/components/Forms/Filters"
import Draggable from "vuedraggable"
import Files from "@/api/files"
export default {
  name: "DatasetForm",
  props: {
    item: { type: Object, default: undefined }
  },
  components: {
    AxDialog,
    Filters,
    Draggable
  },
  data() {
    return {
      panels: [0],
      drag: false,
      valid: false,
      extended: false,
      validating: false,
      results: 0,
      files: [],
      working: false,
      dataset: {
        name: null,
        completion: null,
        production: null,
        economic: null,
        identifier: null,
        filters: []
      },
      rules: [v => !!v || "Field is required"],
      
      selected: undefined,
      
      columns: {
        completion: null,
        production: null
      },
      uniques: []
    }
  },
  created() {
    Files.list().then(response => this.files = response.data.results.map(f => (({ id, uuid, name }) => ({ id, uuid, name }))(f)))
    if (this.item !== undefined) {
      this.dataset = Object.assign({}, this.item)
      this.panels = [0,1]
    }
  },
  computed: {
    isValidStep1() {
      return !!this.dataset.name && !!this.dataset.completion && !!this.dataset.identifier
    },
    validated() {
      if (this.dataset.filters === null) return undefined
      if (this.dataset.filters.length == 0) return undefined

      let condition = 0
      let operator = 0
      let container = 0

      this.dataset.filters.forEach(f => {
        if (this.isObject(f)) {
          condition += 1
        } else {
          if (["and", "or"].includes(f)) {
            operator += 1
          } else if (f == "(") {
            container += 1
          } else if (f == ")") {
            container -= 1
          }
        }
      })

      if (container !== 0) return false
      if ((condition + operator) % 2 == 0) return false
      return true
    },
    mode() {
      return this.item === undefined ? "create" : "edit"
    },
    file_names() {
      if (this.files) return this.files.map(f => (({ id, uuid, name }) => ({ id, uuid, name }))(f))
      return []
    },
    filter_files() {
      if (!this.isEmpty(this.dataset.completion)) {
        if (!this.isEmpty(this.dataset.production)) {
          return [
            { id: "completion", value: this.dataset.completion.name },
            { id: "production", value: this.dataset.production.name }
          ]
        } else {
          return [{ id: "completion", value: this.dataset.completion.name }]
        }
      }
      return []
    },
    well_identifier() {
      if (this.columns.completion) {
        if (this.columns.production) {
          return this.columns.completion.filter(value => this.columns.production.includes(value))
        } else {
          return this.columns.completion
        }
      }
      return []
    }
  },
  watch: {
    "dataset.completion": function(val) {
      if (val) {
        Files.columns(this.dataset.completion.uuid).then(response => (this.columns.completion = response.data))
      } else {
        this.columns.completion = null
      }
    },
    "dataset.production": function(val) {
      if (val) {
        Files.columns(this.dataset.production.uuid).then(response => (this.columns.production = response.data))
      } else {
        this.columns.production = null
      }
    },
    
  },
  methods: {
    onSubmit() {
      this.$emit("submitted", {
        name: this.dataset.name,
        completion: this.dataset.completion.id,
        production: this.dataset.production ? this.dataset.production.id : null,
        economic: this.dataset.economic ? this.dataset.economic.id : null,
        identifier: this.dataset.identifier,
        filters: !this.isEmpty(this.dataset.filters) ? this.dataset.filters : null
      })
    },
    onAddFilter(filter) {
      if (this.dataset.filters === null) this.dataset.filters = []
      this.dataset.filters.push(filter)
    },
    insertOperator(operator) {
      if (this.selected !== undefined) {
        this.selected = this.selected + 1
        this.dataset.filters.splice(this.selected, 0, operator)
      } else {
        this.dataset.filters.push(operator)
        this.selected = this.dataset.filters.length - 1
      }
    },
    deleteItem() {
      this.dataset.filters.splice(this.selected, 1)
      this.selected = this.selected - 1
    },
    filter_value(filter) {
      if (Array.isArray(filter.value)) {
        if (filter.value.length > 1) {
          return `(${filter.value.join(",")})`
        } else {
          return filter.value[0]
        }
      } else {
        return filter.value
      }
    }
  }
}
</script>